/**
 * Financing Simulator - Unified Module
 * Manages both the financing summary display and interactive modal
 * with shared API calls to prevent duplicate requests
 */

// ============================================================================
// SHARED FINANCING DATA SERVICE
// ============================================================================

class FinancingDataService {
  static instance = null;
  static cache = new Map();

  static API = {
    clientID: "storefrontCheckoutExtensions",
    clientSecret: "3E8G85G2Ix8YHoimjCi3p6zH",
    endpoint:
      window.location.host === "canarias.mediamarkt.es"
        ? "https://we-ecommerce-canarias-api.azurewebsites.net/v1/extensions/financing/rates"
        : "https://we-ecommerce-canarias-api-perf.azurewebsites.net/v1/extensions/financing/rates",
  };

  static QUOTES = [
    { months: "3", price: 69.69 },
    { months: "6", price: 249.99 },
    { months: "12", price: 499.99 },
    { months: "18", price: 1499.98 },
    { months: "20", price: 1999.98 },
    { months: "24", price: 2499.98 },
    { months: "30", price: 2999.99 },
    { months: "24", price: 15000.0 },
  ];

  /**
   * Get OAuth token for API authentication
   */
  static async getToken() {
    try {
      // @ts-ignore
      const token = await mmkt.core.oauth.getToken({
        client_id: this.API.clientID,
        client_secret: this.API.clientSecret,
      });
      return token;
    } catch (error) {
      console.error("[FinancingDataService] Error getting token:", error);
      throw error;
    }
  }

  /**
   * Fetch financing rates from API with caching
   * @param {Object} params - Request parameters
   * @param {number} params.amount - Product price
   * @param {string} params.method - Financing method ("financing" | "pay_in_x_time")
   * @param {number} params.productId - Product ID
   * @param {number|null} params.customerId - Customer ID (optional)
   */
  static async getRates({ amount, method, productId, customerId = null }) {
    // Create cache key
    const cacheKey = `${productId}_${amount}_${method}_${customerId}`;

    // Return cached data if available
    if (this.cache.has(cacheKey)) {
      console.log("[FinancingDataService] Returning cached rates for:", cacheKey);
      return this.cache.get(cacheKey);
    }

    console.log("[FinancingDataService] Fetching rates from API:", { amount, method, productId });

    try {
      const token = await this.getToken();

      const response = await fetch(this.API.endpoint, {
        method: "POST",
        headers: {
          authorization: `JWT ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          customerId: customerId || null,
          amount,
          method,
          productsIds: [productId],
        }),
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data = await response.json();

      // Cache the result
      this.cache.set(cacheKey, data.rates);

      return data.rates;
    } catch (error) {
      console.error("[FinancingDataService] Error fetching rates:", error);
      throw error;
    }
  }

  /**
   * Clear cache (useful for testing or when product changes)
   */
  static clearCache() {
    this.cache.clear();
  }

  /**
   * Sort list by numeric property
   */
  static sortListByNumber(list, prop, order = "asc") {
    return list.sort((a, b) => {
      if (order === "asc") return a?.[prop] - b?.[prop];
      else return b?.[prop] - a?.[prop];
    });
  }
}

// ============================================================================
// FINANCING SUMMARY WEB COMPONENT
// ============================================================================

class MMKTFinancingSummary extends HTMLElement {
  /**
   * @type {number}
   */
  productPrice;
  /**
   * @type {"financing" | "pay_in_x_time"}
   */
  financingType;
  /**
   * @type {number | null}
   */
  customerId;
  /**
   * @type {number}
   */
  productId;

  constructor() {
    super();

    this.dataSetToProperties([
      "productPrice",
      "customerId",
      "productId",
      "financingType",
    ]);

    this.checkForMandatoryProperties([
      "productPrice",
      "productId",
      "financingType",
    ]);

    this.init();
  }

  /**
   * Check if string contains only numbers
   */
  checkForOnlyNumbersInStr(str) {
    if (!str) return false;
    return /^\d+$/.test(str);
  }

  /**
   * Convert dataset attributes to component properties
   */
  dataSetToProperties(keys) {
    keys.forEach((key) => {
      const value = this.dataset[key];
      const isOnlyNumbers = this.checkForOnlyNumbersInStr(value);

      this[key] =
        typeof value === "string" && !isOnlyNumbers
          ? value
          : JSON.parse(value || null);
    });
  }

  /**
   * Validate mandatory properties
   */
  checkForMandatoryProperties(keys) {
    const missingProp = keys.find((key) => !this[key]);

    if (missingProp)
      throw new Error(
        `${this.constructor.name} couldn't be initialized without ${missingProp}.`
      );
  }

  /**
   * Replace values from data object to DOM elements
   */
  replaceValuesFromDataToDom(data, selectorPropPairs, parent) {
    selectorPropPairs.forEach(([selector, propName]) => {
      const elements = parent.querySelectorAll(selector);

      if (
        elements.length &&
        data[propName] !== undefined &&
        data[propName] !== null
      )
        elements.forEach((el) => (el.innerHTML = data[propName]));
      else
        console.warn(
          `Property ${propName} couldn't be replaced, this could be because either there is not an element with selector ${selector} or the data provided doesn't have this property.`
        );
    });
  }

  /**
   * Set rate information in DOM
   */
  setRateInDom(rate) {
    this.replaceValuesFromDataToDom(
      rate,
      [
        ["#summary-tin", "porTin"],
        ["#summary-tae", "porTae"],
        ["#summary-months", "numPlazos"],
        [
          "#summary-amount",
          this.financingType === "pay_in_x_time"
            ? "ultimaCuota"
            : "cuotasRestantes",
        ],
      ],
      this
    );

    const summaryTableEl = document.querySelector(
      `.mmkt-financing-table-summary[data-financing-type="${this.financingType}"]`
    );

    if (summaryTableEl) {
      rate.creditValue = parseFloat(
        rate.impTotalAdeudado - rate.impAFinanciar
      ).toFixed(2);

      this.replaceValuesFromDataToDom(
        rate,
        [
          ["#summary-table-minimum", "impAFinanciar"],
          ["#summary-table-tin", "porTin"],
          ["#summary-table-tae", "porTae"],
          [
            "#summary-table-first-payment",
            this.financingType === "pay_in_x_time"
              ? "primeraCuota"
              : "cuotasRestantes",
          ],
          [
            "#summary-table-monthly-payment",
            this.financingType === "pay_in_x_time"
              ? "ultimaCuota"
              : "cuotasRestantes",
          ],
          ["#summary-table-num-payments", "numPlazos"],
          ["#summary-table-total-amount", "impTotalAdeudado"],
          ["#summary-table-credit-value", "creditValue"],
          ["#summary-table-footer-minimum", "impAFinanciar"],
          ["#summary-table-footer-tin", "porTin"],
          ["#summary-table-footer-tae", "porTae"],
          [
            "#summary-table-footer-month",
            this.financingType === "pay_in_x_time"
              ? "ultimaCuota"
              : "cuotasRestantes",
          ],
          ["#summary-table-footer-quotes", "numPlazos"],
          ["#summary-table-footer-total", "impTotalAdeudado"],
          ["#summary-table-footer-credit-value", "creditValue"],
          ["#summary-table-title-quotes", "numPlazos"],
        ],
        summaryTableEl
      );
    }
  }

  /**
   * Initialize the financing summary
   */
  async init() {
    try {
      // Use shared service to get rates
      const financingData = await FinancingDataService.getRates({
        amount: this.productPrice,
        method: this.financingType,
        productId: this.productId,
        customerId: this.customerId,
      });

      FinancingDataService.sortListByNumber(
        FinancingDataService.QUOTES,
        "price",
        "asc"
      );

      const quote = FinancingDataService.QUOTES.find(
        (q) => q.price >= this.productPrice
      );

      let rateToShowAsSummary = financingData?.find((rate) => {
        if (this.financingType === "pay_in_x_time") return rate.numPlazos == 3;
        return rate.numPlazos == quote?.months;
      });

      if (!rateToShowAsSummary && financingData?.length) {
        rateToShowAsSummary = financingData[0];
      }

      if (rateToShowAsSummary) {
        const isFinancingZero =
          rateToShowAsSummary.porTin === 0 && rateToShowAsSummary.porTae === 0;

        if (isFinancingZero) {
          const quotesEl = this.querySelector(".mmkt-financing-quotes");
          const summaryEl = this.querySelector(".mmkt-financing-summary-info");
          const spanEl = quotesEl?.querySelector("span");

          if (spanEl) {
            spanEl.innerHTML = spanEl.innerHTML.replace(":", " - 0%");
          }

          quotesEl?.classList.add("mmkt-financing-quotes-zero");
          summaryEl?.classList.add("mmkt-financing-quotes-zero");
        }

        this.setRateInDom(rateToShowAsSummary);

        setTimeout(() => {
          const summariesEls = document.querySelectorAll("#summary-months");

          const rateWithLessNumPlazos = Array.from(summariesEls).find(
            (summary) =>
              parseInt(summary.innerHTML) <
              parseInt(rateToShowAsSummary.numPlazos)
          );

          if (rateWithLessNumPlazos) {
            const lessValue = parseInt(rateWithLessNumPlazos.innerHTML);

            rateToShowAsSummary = financingData?.find((rate) => {
              return rate.numPlazos == lessValue;
            });

            if (rateToShowAsSummary) {
              this.setRateInDom(rateToShowAsSummary);
            }
          }
          this.classList.remove("mmkt-financing-summary-loading");
        }, 700);
      } else {
        this.remove();
      }
    } catch (error) {
      console.error("[MMKTFinancingSummary] Error initializing:", error);
      this.remove();
    }
  }
}

// ============================================================================
// FINANCING MODAL WEB COMPONENT
// ============================================================================

class MMKTFinancingModal extends HTMLElement {
  /**
   * @type {HTMLElement | null}
   */
  modalContainer = null;
  /**
   * @type {boolean}
   */
  initialized = false;

  constructor() {
    super();
    this.init();
  }

  /**
   * Get global financing configuration from window
   */
  getGlobalConfig() {
    return {
      totalAmount: window.financingTotalAmount,
      customerId: window.customerId,
      productId: window.productId,
      messages: window.financingSimulatorMessages,
    };
  }

  /**
   * Close modal
   */
  closeModal() {
    this.modalContainer?.classList.add("hidden");
  }

  /**
   * Open modal and load data if needed
   */
  async openModal() {
    this.modalContainer?.classList.remove("hidden");

    // Load data on first open (lazy loading)
    if (!this.initialized) {
      await this.loadInitialData();
      this.initialized = true;
    }
  }

  /**
   * Render installment options
   */
  renderInstallments(installments, method) {
    const installmentsContainer = this.querySelector(
      ".financing-simulator-options"
    );

    if (!installmentsContainer) return;

    installmentsContainer.innerHTML = "";

    const summariesEls = document.querySelectorAll("#summary-months");

    const minQuote = Array.from(summariesEls).reduce((prev, current) => {
      const quote = parseInt(current.innerHTML);

      if (!isNaN(quote)) {
        if (!prev || quote < prev) {
          prev = quote;
        }
      }

      return prev;
    }, null);

    const quote = FinancingDataService.QUOTES.find((q) => q.months == minQuote);

    // Filter unique installments by numPlazos (months) to avoid duplicates
    // For pay_in_x_time: prefer rates with TAE/TIN = 0% (promotional rates)
    // Otherwise keep the first occurrence of each unique month value
    const uniqueInstallments = installments.reduce((acc, current) => {
      const existing = acc.find(item => item.numPlazos === current.numPlazos);

      if (!existing) {
        acc.push(current);
      } else if (method === "pay_in_x_time") {
        // Replace existing if current has better rate (0% TAE/TIN)
        const currentIsZero = current.porTae === 0 || current.porTae === "0";
        const existingIsZero = existing.porTae === 0 || existing.porTae === "0";

        if (currentIsZero && !existingIsZero) {
          const index = acc.indexOf(existing);
          acc[index] = current;
        }
      }

      return acc;
    }, []);

    uniqueInstallments.forEach((installment) => {
      const option = document.createElement("div");

      // Prefix ID with 'rate-' to ensure it doesn't start with a number (invalid CSS selector)
      option.id = `rate-${installment.codTarifa}_${installment.numPlazos}`;
      option.classList.add("financing-simulator-option");
      option.innerHTML = /* HTML */ `
        <small>${installment.numPlazos}</small>
        <span>Meses</span>
      `;

      installmentsContainer.appendChild(option);

      // Auto-select default option
      if (method === "pay_in_x_time" && installment.numPlazos === "3") {
        this.changeRateSelected(installment, method);
      } else if (
        method === "financing" &&
        quote?.months === installment.numPlazos
      ) {
        this.changeRateSelected(installment, method);
      }

      option.addEventListener("click", () =>
        this.changeRateSelected(installment, method)
      );
    });
  }

  /**
   * Change selected rate and update details
   */
  changeRateSelected(installment, financingType) {
    const prevSelected = this.querySelector(
      ".financing-simulator-option.selected"
    );
    const newSelected = this.querySelector(
      `#rate-${installment.codTarifa}_${installment.numPlazos}`
    );

    prevSelected?.classList.remove("selected");
    newSelected?.classList.add("selected");

    this.renderInstallmentDetails(installment, financingType);
  }

  /**
   * Render installment details in modal footer
   */
  renderInstallmentDetails(installment, financingType) {
    const numCuotasEl = this.querySelector("#num_cuotas");
    const taeEl = this.querySelector("#tae");
    const cuotaEl = this.querySelector("#importe_cuota");
    const ultimaCuotaEl = this.querySelector("#ultima_cuota");
    const totalEl = this.querySelector("#total_adeudado");
    const tinEl = this.querySelector("#tin");

    if (numCuotasEl) numCuotasEl.innerHTML = installment.numPlazos;
    if (taeEl) taeEl.innerHTML = `${installment.porTae}%`;
    if (cuotaEl)
      cuotaEl.innerHTML = `${
        financingType === "pay_in_x_time"
          ? installment.ultimaCuota
          : installment.cuotasRestantes
      }€`;
    if (ultimaCuotaEl)
      ultimaCuotaEl.innerHTML = `${
        financingType === "pay_in_x_time"
          ? installment.primeraCuota
          : installment.cuotasRestantes
      }€`;
    if (totalEl) totalEl.innerHTML = `${installment.impTotalAdeudado}€`;
    if (tinEl) tinEl.innerHTML = `${installment.porTin}%`;
  }

  /**
   * Load financing data for a specific method
   */
  async loadFinancingData(method) {
    const tabContainer = this.querySelector(".financing-simulator-tabs");
    const installmentsContainer = this.querySelector(
      ".financing-simulator-options"
    );

    if (installmentsContainer) {
      installmentsContainer.innerHTML = /* HTML */ `<span style="width: 100%;"
        >Obteniendo tarifas...</span
      >`;
    }

    tabContainer?.classList.add("loading");

    try {
      const config = this.getGlobalConfig();

      // Use shared service - will use cache if available
      const rates = await FinancingDataService.getRates({
        amount: config.totalAmount,
        method,
        productId: config.productId,
        customerId: config.customerId,
      });

      tabContainer?.classList.remove("loading");

      if (rates) {
        this.renderInstallments(rates, method);
      }

      return rates;
    } catch (error) {
      console.error("[MMKTFinancingModal] Error loading financing data:", error);
      tabContainer?.classList.remove("loading");
      if (installmentsContainer) {
        installmentsContainer.innerHTML = /* HTML */ `<span style="width: 100%;"
          >Error al cargar tarifas</span
        >`;
      }
    }
  }

  /**
   * Load initial data based on active tab
   */
  async loadInitialData() {
    const config = this.getGlobalConfig();

    if (!config.totalAmount || !config.productId || !config.messages) {
      console.log("[MMKTFinancingModal] Global variables not available");
      return;
    }

    const tabs = this.querySelectorAll(".financing-simulator-tab");
    const tabSelected = Array.from(tabs).find((tab) =>
      tab.classList.contains("active")
    );

    const method = tabSelected?.dataset?.financingMethod || "financing";

    try {
      await this.loadFinancingData(method);
    } catch (error) {
      console.error("[MMKTFinancingModal] Error loading initial data:", error);
    }
  }

  /**
   * Setup tab switching functionality
   */
  setupTabs() {
    const tabs = this.querySelectorAll(".financing-simulator-tab");
    const ultimaOPrimeraCuotaLabel = this.querySelector(
      "#ultima_o_primera_cuota_label"
    );

    tabs.forEach((tab) => {
      tab.addEventListener("click", async () => {
        const method = tab.dataset.financingMethod;
        const config = this.getGlobalConfig();

        const messageEl = this.querySelector(
          ".financing-simulator-info-section span"
        );
        const legalEl = this.querySelector(
          ".financing-simulator-legal-section span"
        );
        const prevActive = this.querySelector(
          ".financing-simulator-tab.active"
        );

        prevActive?.classList.remove("active");
        tab.classList.add("active");

        if (method === "pay_in_x_time") {
          if (ultimaOPrimeraCuotaLabel)
            ultimaOPrimeraCuotaLabel.innerText = "Primera cuota";
        } else {
          if (ultimaOPrimeraCuotaLabel)
            ultimaOPrimeraCuotaLabel.innerText = "Última cuota";
        }

        try {
          const newInfoMessage = config.messages[method]["info"];
          const newLegalMessage = config.messages[method]["legal"];

          if (messageEl) messageEl.innerHTML = newInfoMessage;
          if (legalEl) legalEl.innerHTML = newLegalMessage;

          const installments = await this.loadFinancingData(method);

          if (installments) {
            setTimeout(() => {
              this.renderInstallments(installments, method);
            }, 900);
          }
        } catch (error) {
          console.error("[MMKTFinancingModal] Error changing tab:", error);
        }
      });
    });
  }

  /**
   * Initialize modal functionality
   */
  init() {
    this.modalContainer = this.querySelector(
      ".financing-simulator-modal-container"
    );

    if (!this.modalContainer) {
      console.log("[MMKTFinancingModal] Modal container not found");
      return;
    }

    // Setup close button
    const closeButton = this.querySelector(".financing-simulator-close-btn");
    closeButton?.addEventListener("click", () => this.closeModal());

    // Setup open buttons
    const openButton = document.querySelector(
      ".mmkt-financing-simulator-open-button"
    );
    const moreOptionsButton = document.querySelector(
      ".more-financing-options"
    );

    openButton?.addEventListener("click", () => this.openModal());
    moreOptionsButton?.addEventListener("click", () => this.openModal());

    // Setup tabs
    this.setupTabs();

    console.log("[MMKTFinancingModal] Initialized (lazy loading enabled)");
  }
}

// ============================================================================
// REGISTER WEB COMPONENTS
// ============================================================================

customElements.define("mmkt-financing-summary", MMKTFinancingSummary);
customElements.define("mmkt-financing-modal", MMKTFinancingModal);

// Export for external use if needed
if (typeof module !== "undefined" && module.exports) {
  module.exports = {
    FinancingDataService,
    MMKTFinancingSummary,
    MMKTFinancingModal,
  };
}
